import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import CodePen from "../../components/Codepen/Codepen";

export default function S03P02({ reveal }: ISlideObject) {
  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 03 Parte 2"
      />
      <DefaultSlide title="Agenda">
        <div>
          <ul>
            <li>Responsive Web Design</li>
            <li>Uso de bibliotecas CSS</li>
          </ul>
        </div>
      </DefaultSlide>

      <section>
        <TitleSlide title="Responsive Web Design" />
        <ImageSlide image="/images/S03/rwd.jpg"></ImageSlide>
        <DefaultSlide title="¿Qué es Responsive Web Design?">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              <strong>Responsive Web Design</strong> es una técnica de diseño
              web que busca la correcta visualización de una misma página en
              distintos dispositivos. Desde computadoras de escritorio hasta
              tablets y móviles.
            </p>
            <p>
              Con el crecimiento de dispositivos móviles, es importante que
              nuestra página web se adapte a distintos tamaños de pantalla.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Por qué es importante?">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              <strong>Responsive Web Design</strong> es importante porque
              permite a los desarrolladores de sitios web crear una experiencia
              de usuario óptima en dispositivos móviles, sin tener que rediseñar
              la página web para cada dispositivo.
            </p>
            <p>
              Un sitio web diseñado con RWD se adapta a la vista del dispositivo
              del usuario. Esto significa que si el usuario abre el sitio en su
              teléfono móvil, la página se verá igual que si la abriera en su
              computadora de escritorio.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Cómo se logra?">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              <strong>Responsive Web Design</strong> se logra mediante el uso de
              <strong>media queries</strong> y <strong>flexible grids</strong>.
            </p>
            <p>
              Las media queries permiten aplicar estilos CSS basados en las
              características del dispositivo, como el ancho de la pantalla.
            </p>
            <p>
              Los flexible grids permiten crear diseños que se adaptan al tamaño
              de la pantalla, en lugar de fijar tamaños en píxeles. Esto se
              logra mediante el uso de porcentajes y unidades relativas.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Media Queries">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              Las media queries son una técnica de CSS que permite aplicar
              estilos basados en las características del dispositivo.
            </p>
            <p>
              Por ejemplo, se puede aplicar un estilo diferente si la pantalla
              es más pequeña que cierto tamaño, o si el dispositivo es un móvil.
            </p>
            <p>
              Las media queries se definen con la regla{" "}
              <code>@media screen and (max-width: 600px) {"{"}</code>
              <br />
              <code>{"}"}</code>
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Flexible Grids">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              Los flexible grids permiten crear diseños que se adaptan al tamaño
              de la pantalla.
            </p>
            <p>
              En lugar de fijar tamaños en píxeles, se utilizan porcentajes y
              unidades relativas.
            </p>
            <p>
              Por ejemplo, en lugar de fijar el ancho de un elemento en 300px,
              se puede fijar en 50% para que ocupe la mitad del ancho de la
              pantalla.
            </p>
          </div>
        </DefaultSlide>
        <CodeSlide title="Ejemplo de Media Queries" language="css">
          {`  @media screen and (max-width: 600px) {
    body {
      background-color: lightblue;
    }
  }`}
        </CodeSlide>
        <DefaultSlide title="Viewport">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              El <strong>viewport</strong> es el área visible de una página web
              en un dispositivo. Es la ventana a través de la cual los usuarios
              ven el contenido de la página.
            </p>
            <p>
              En dispositivos móviles, el viewport es más pequeño que en
              dispositivos de escritorio, por lo que es importante adaptar el
              diseño para que se vea bien en todos los tamaños de pantalla.
            </p>
            <p>
              Para controlar el viewport, se utiliza la meta etiqueta{" "}
              <code>{`<meta name="viewport" content="width=device-width, initial-scale=1.0">`}</code>
              .
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Mobile-First Design">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              <strong>Mobile-First Design</strong> es una estrategia de diseño
              web en la que se comienza diseñando para dispositivos móviles y
              luego se adapta el diseño para dispositivos de mayor tamaño.
            </p>
            <p className="fragment">
              Esta estrategia asegura que la experiencia de usuario sea óptima
              en dispositivos móviles, que son los más utilizados hoy en día.
            </p>
            <p className="fragment">
              Al diseñar primero para móviles, se prioriza el contenido y la
              funcionalidad esencial, evitando sobrecargar la página con
              elementos innecesarios.
            </p>
          </div>
        </DefaultSlide>
      </section>

      <section>
        <TitleSlide title="Ejercicio HTML & CSS - Parte 2" />
        <DefaultSlide title="Ejercicio HTML & CSS">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              Hacer el sitio web responsive. Ver CSS Tricks para flex.
              <img src="/images/S03/exercise.png" alt="" />
            </p>
          </div>
        </DefaultSlide>
      </section>

      <section>
        <TitleSlide title="Uso de bibliotecas CSS" />
        <DefaultSlide title="¿Por qué son importantes las bibliotecas CSS?">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              Las bibliotecas CSS son importantes porque facilitan y aceleran el
              proceso de desarrollo web.
            </p>
            <p>
              Proveen estilos y componentes predefinidos que se pueden reutilizar,
              lo que ahorra tiempo y esfuerzo.
            </p>
            <p>
              Además, aseguran consistencia en el diseño y ayudan a mantener
              buenas prácticas de desarrollo.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Bootstrap">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              <strong>Bootstrap</strong> es una biblioteca de CSS y JavaScript
              que facilita el diseño de sitios web responsivos y móviles.
            </p>
            <p>
              Bootstrap incluye una serie de componentes y estilos predefinidos
              que se pueden utilizar para crear diseños atractivos y
              funcionales.
            </p>
            <p>
              Al utilizar Bootstrap, se ahorra tiempo y esfuerzo en el diseño y
              desarrollo de un sitio web, ya que se pueden reutilizar los
              componentes y estilos predefinidos.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ventajas de Bootstrap">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              <strong>Bootstrap</strong> tiene varias ventajas, entre las que se
              incluyen:
            </p>
            <ul>
              <li>Facilidad de uso</li>
              <li>Reutilización de componentes</li>
              <li>Compatibilidad con navegadores</li>
              <li>Documentación extensa</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Desventajas de Bootstrap">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              <strong>Bootstrap</strong> también tiene algunas desventajas, como:
            </p>
            <ul>
              <li>Tamaño del archivo</li>
              <li>Personalización limitada</li>
              <li>Dependencia de JavaScript</li>
              <li>Estilos genéricos</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Alternativas a Bootstrap">
          <div style={{ width: "100%", fontSize: "0.7em" }}>
            <p>
              Si no quieres utilizar Bootstrap, hay varias alternativas que
              puedes considerar:
            </p>
            <ul>
              <li>Tailwind
              </li>
              <li>Bulma</li>
              <li>Foundation</li>
              <li>Materialize</li>
            </ul>
          </div>
        </DefaultSlide>
        <section>
          <h3>Demo de Bootstrap</h3>
        </section>
        <section>
          <img src="/images/S03/bootstrap.png" alt="Sitio web de tienda con bootstrap." />
        </section>
      </section>

      <section>
        Se publicará la especificación del primer laboratorio mañana Viernes 7 de Marzo al medio día
      </section>


      <ImageSlide image="/images/S03/dancing3.gif" title="¡Fin de la clase!">
        <p>Recuerda completar todos los contenidos de la semana.</p>
        {/* <button className="btn btn-success">Marcar como completo</button> */}
      </ImageSlide>
    </>
  );
}
